import React from "react";
import styled from "@emotion/styled";

import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useUserInfo } from "../../hooks/useUserInfo";
import { Theme } from "../../theme";

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3rem;
`;

const Intro = styled.div`
	max-width: 760px;
`;

const SectionLabel = styled.p<{ theme: Theme }>`
	margin: 0 0 0.5rem;
	color: ${props => props.theme.colors.primary.main};
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
`;

const Title = styled.h2<{ theme: Theme }>`
	margin: 0;
	color: ${props => props.theme.colors.text.primary};
	font-size: ${props => props.theme.typography.roles.sectionHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.sectionHeading.fontWeight};
	line-height: ${props => props.theme.typography.roles.sectionHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.sectionHeading.letterSpacing};
`;

const Description = styled.p<{ theme: Theme }>`
	margin: 0.75rem 0 0;
	color: ${props => props.theme.colors.text.secondary};
	font-size: ${props => props.theme.typography.roles.body.fontSize};
	line-height: ${props => props.theme.typography.roles.body.lineHeight};
	white-space: pre-wrap;
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const SectionHeading = styled.div`
	display: flex;
	align-items: baseline;
	gap: 0.75rem;
`;

const SectionIndex = styled.span<{ theme: Theme }>`
	color: ${props => props.theme.colors.primary.main};
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	font-weight: 600;
`;

const SectionTitle = styled.h3<{ theme: Theme }>`
	margin: 0;
	color: ${props => props.theme.colors.text.primary};
	font-size: ${props => props.theme.typography.roles.title.fontSize};
	font-weight: ${props => props.theme.typography.roles.title.fontWeight};
	line-height: ${props => props.theme.typography.roles.title.lineHeight};
`;

const LanguagesGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0.875rem;

	@media (max-width: 760px) {
		grid-template-columns: 1fr;
	}
`;

const Surface = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.density.cardRadius};
	box-shadow: ${props => props.theme.shadows.base};
`;

const LanguageCard = styled(Surface)`
	padding: 1.125rem;
	transition: border-color 0.2s ease, transform 0.2s ease;

	&:hover {
		border-color: ${props => props.theme.colors.primary.main};
		transform: translateY(-2px);
	}
`;

const LanguageHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 0.875rem;
`;

const LanguageMark = styled.div<{ theme: Theme }>`
	display: grid;
	place-items: center;
	width: 2.75rem;
	height: 2.75rem;
	flex: 0 0 2.75rem;
	color: ${props => props.theme.colors.primary.main};
	background: ${props => props.theme.colors.background.tertiary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 0.75rem;
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: 0.8rem;
	font-weight: 700;
	letter-spacing: -0.03em;
`;

const LanguageIdentity = styled.div`
	min-width: 0;
	flex: 1;
`;

const LanguageName = styled.h4<{ theme: Theme }>`
	margin: 0;
	color: ${props => props.theme.colors.text.primary};
	font-size: 1.0625rem;
	font-weight: 650;
`;

const LanguageMeta = styled.p<{ theme: Theme }>`
	margin: 0.2rem 0 0;
	color: ${props => props.theme.colors.text.tertiary};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
`;

const Level = styled.span<{ theme: Theme }>`
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
`;

const LanguageDescription = styled.p<{ theme: Theme }>`
	margin: 0.875rem 0;
	color: ${props => props.theme.colors.text.secondary};
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	line-height: ${props => props.theme.typography.roles.bodySmall.lineHeight};
`;

const Stack = styled.dl`
	display: grid;
	grid-template-columns: 5.5rem minmax(0, 1fr);
	gap: 0.45rem 0.75rem;
	margin: 0;
`;

const StackLabel = styled.dt<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	line-height: 1.55;
`;

const StackValue = styled.dd<{ theme: Theme }>`
	margin: 0;
	color: ${props => props.theme.colors.text.primary};
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	line-height: 1.55;
`;

const CapabilitiesGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.875rem;

	@media (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 560px) {
		grid-template-columns: 1fr;
	}
`;

const CapabilityCard = styled(Surface)`
	padding: 1rem;
`;

const CapabilityName = styled.h4<{ theme: Theme }>`
	margin: 0;
	color: ${props => props.theme.colors.text.primary};
	font-size: 0.95rem;
	font-weight: 650;
`;

const CapabilityDetail = styled.p<{ theme: Theme }>`
	margin: 0.4rem 0 0;
	color: ${props => props.theme.colors.text.secondary};
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	line-height: 1.55;
`;

const SystemsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.875rem;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

const SystemPanel = styled(Surface)`
	padding: 1rem;
`;

const SystemTitle = styled.h4<{ theme: Theme }>`
	margin: 0 0 0.75rem;
	color: ${props => props.theme.colors.text.primary};
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	font-weight: 650;
	letter-spacing: 0.04em;
	text-transform: uppercase;
`;

const SystemList = styled.ul`
	display: flex;
	flex-wrap: wrap;
	gap: 0.45rem 0.75rem;
	margin: 0;
	padding: 0;
	list-style: none;
`;

const SystemItem = styled.li<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	line-height: 1.45;
`;

const getMonogram = (name: string) => {
	if (name.toLowerCase().includes("typescript")) return "TS";
	if (name.toLowerCase().includes("swift")) return "SW";
	return name.slice(0, 2).toUpperCase();
};

const TechStack: React.FC = () => {
	const { t } = useLanguage();
	const { theme } = useTheme();
	const info = useUserInfo();

	return (
		<Container>
			<Intro>
				<SectionLabel theme={theme}>Technical profile</SectionLabel>
				<Title theme={theme}>{t.about.techStack.title}</Title>
				<Description theme={theme}>{t.about.techStack.description}</Description>
			</Intro>

			<Section>
				<SectionHeading>
					<SectionIndex theme={theme}>01</SectionIndex>
					<SectionTitle theme={theme}>Core languages</SectionTitle>
				</SectionHeading>
				<LanguagesGrid>
					{info.techStack.languages.map((language) => (
						<LanguageCard key={language.name} theme={theme}>
							<LanguageHeader>
								<LanguageMark theme={theme}>{getMonogram(language.name)}</LanguageMark>
								<LanguageIdentity>
									<LanguageName theme={theme}>{language.name}</LanguageName>
									<LanguageMeta theme={theme}>
										<Level theme={theme}>{language.level}</Level> · {language.yearsOfExperience} years
									</LanguageMeta>
								</LanguageIdentity>
							</LanguageHeader>
							<LanguageDescription theme={theme}>{language.description}</LanguageDescription>
							<Stack>
								{language.frameworks?.length > 0 && (
									<><StackLabel theme={theme}>Frameworks</StackLabel><StackValue theme={theme}>{language.frameworks.join(" · ")}</StackValue></>
								)}
								{language.tools?.length > 0 && (
									<><StackLabel theme={theme}>Tools</StackLabel><StackValue theme={theme}>{language.tools.join(" · ")}</StackValue></>
								)}
								{language.architecture?.length > 0 && (
									<><StackLabel theme={theme}>Architecture</StackLabel><StackValue theme={theme}>{language.architecture.join(" · ")}</StackValue></>
								)}
							</Stack>
						</LanguageCard>
					))}
				</LanguagesGrid>
			</Section>

			<Section>
				<SectionHeading>
					<SectionIndex theme={theme}>02</SectionIndex>
					<SectionTitle theme={theme}>Capabilities</SectionTitle>
				</SectionHeading>
				<CapabilitiesGrid>
					{info.techStack.platforms.map((platform) => (
						<CapabilityCard key={platform.name} theme={theme}>
							<CapabilityName theme={theme}>{platform.name}</CapabilityName>
							<CapabilityDetail theme={theme}>{platform.technologies.join(" · ")}</CapabilityDetail>
						</CapabilityCard>
					))}
				</CapabilitiesGrid>
			</Section>

			<Section>
				<SectionHeading>
					<SectionIndex theme={theme}>03</SectionIndex>
					<SectionTitle theme={theme}>Infrastructure &amp; systems</SectionTitle>
				</SectionHeading>
				<SystemsGrid>
					<SystemPanel theme={theme}>
						<SystemTitle theme={theme}>Data</SystemTitle>
						<SystemList>{info.techStack.databases.map((item) => <SystemItem key={item.name} theme={theme}>{item.name} · {item.type}</SystemItem>)}</SystemList>
					</SystemPanel>
					<SystemPanel theme={theme}>
						<SystemTitle theme={theme}>Delivery</SystemTitle>
						<SystemList>{info.techStack.devOps.map((item) => <SystemItem key={item} theme={theme}>{item}</SystemItem>)}</SystemList>
					</SystemPanel>
					<SystemPanel theme={theme}>
						<SystemTitle theme={theme}>Specialties</SystemTitle>
						<SystemList>{info.techStack.other.map((item) => <SystemItem key={item} theme={theme}>{item}</SystemItem>)}</SystemList>
					</SystemPanel>
				</SystemsGrid>
			</Section>
		</Container>
	);
};

export default TechStack;
